import {
    deleteTask,
    deleteTasks,
    fetchAssigneeIdsWithTasks,
    fetchAllTasksForFilters,
    fetchMapBranches,
    fetchTasksAll,
    fetchTasksYours,
    useGetAllTasks,
    useGetBranchImage,
} from '../utils/dashboard-hooks';
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, TableComponentProps, User } from '../models/table-models';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { Branch, BranchDetails, ItemRequest, Task, UserTasksPage } from '../models/dashboard-models';
import dateFormat from 'dateformat';
import { date, dateTime } from '../utils/Enums/Common';
import { useTranslation } from 'react-i18next';
import { Accordion, Button, Form, Modal, Spinner } from 'react-bootstrap';
import noImage from '../assets/No_image_available.png';
import { TasksContext } from '../context/TasksContext';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../components/CustomToastContent';
import { TaskDetails } from '../components/Tasks/TaskDetails';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../utils/Enums/Roles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools } from '@fortawesome/free-solid-svg-icons/faTools';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import { faCartPlus, faMoneyBill, faUndo, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { getMissingItemsForTasks } from '../utils/warehouses-hooks';
import { MissingItemsForTasks } from './tasks/MissingItemsForTasks';
import { CompleteTaskModal } from '../components/Tasks/CompleteTaskModal';
import ErrorBoundary from '../components/ErrorBoundary';
import { NewRequest } from './NewRequest';
import { NewRequestContext, NewRequestProvider } from '../context/NewRequestContext';
import { useGetUsers } from '../utils/users-hooks';
import { PageSizeComponent } from '../components/PageSizeComponent';
import { usePagination } from '../utils/pagination-hooks';
import { PaginationComponent } from '../components/Pagination';
import { Checkbox } from '@mui/material';
import { InfoModal } from '../components/InfoModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { changeTasksAssignee } from '../utils/work-hooks';

export const Tasks = ({ filter, setFilter }: TableComponentProps) => {
    const location = useLocation();
    const routeState = location.state as { onlyUnfinished?: boolean; filter?: Partial<Filter> } | null;
    const [onlyUnfinished, setOnlyUnfinished] = useState(routeState?.onlyUnfinished ?? true);
    const { hasPermission } = useContext(AuthContext);
    const isDistributor = hasPermission([Role.DISTRIBUTOR, Role.INSTALLER]);
    const isCoordinator = hasPermission([Role.COORDINATOR]);
    const isDistributorView = isCoordinator ? false : isDistributor;
    const { assigneeId } = useParams();

    return (
        <Suspense fallback={<Spinner animation='border' />}>
            <TasksInnerWrapper
                filter={filter}
                setFilter={setFilter}
                onlyUnfinished={onlyUnfinished}
                setOnlyUnfinished={setOnlyUnfinished}
                isDistributor={isDistributorView}
                routeAssigneeId={assigneeId}
                routeFilter={routeState?.filter}
            />
        </Suspense>
    );
};

const BranchTasksResource = ({
    filter,
    onlyUnfinished,
    isDistributor,
    sortDirection,
    setActiveRefetchTasks,
    onTasksRefetched,
    children,
}: {
    filter: Filter;
    onlyUnfinished: boolean;
    isDistributor: boolean;
    sortDirection: 'ASC' | 'DESC';
    setActiveRefetchTasks?: React.Dispatch<React.SetStateAction<() => void>>;
    onTasksRefetched?: () => void;
    children: (props: {
        taskPage: UserTasksPage;
        refetchTasks: () => void;
        pagination: ReturnType<typeof usePagination>;
    }) => JSX.Element;
}) => {
    const taskPagination = usePagination(10);
    const [getAllTasks, fetchAllTasks] = useGetAllTasks(
        onlyUnfinished,
        filter.id,
        filter.before,
        filter.after,
        filter.search,
        isDistributor,
        taskPagination.currentPage,
        taskPagination.pageSize,
        sortDirection
    );

    const refetchTasks = useCallback(() => {
        resourceCache(fetchTasksAll).clear();
        resourceCache(fetchTasksYours).clear();
        resourceCache(fetchMapBranches).clear();
        fetchAllTasks(
            onlyUnfinished,
            filter.id,
            filter.before,
            filter.after,
            filter.search,
            isDistributor,
            taskPagination.currentPage,
            taskPagination.pageSize,
            sortDirection
        );
        onTasksRefetched?.();
    }, [
        fetchAllTasks,
        filter.id,
        filter.before,
        filter.after,
        filter.search,
        isDistributor,
        onTasksRefetched,
        onlyUnfinished,
        sortDirection,
        taskPagination.currentPage,
        taskPagination.pageSize,
    ]);

    useEffect(() => {
        taskPagination.setCurrentPage(1);
    }, [filter.id, filter.before, filter.after, filter.search, onlyUnfinished, sortDirection]);

    useEffect(() => {
        setActiveRefetchTasks?.(() => refetchTasks);
    }, [setActiveRefetchTasks, refetchTasks]);

    return children({ taskPage: getAllTasks(), refetchTasks, pagination: taskPagination });
};

const TasksInnerWrapper = ({
    filter,
    setFilter,
    onlyUnfinished,
    setOnlyUnfinished,
    isDistributor,
    routeAssigneeId,
    routeFilter,
}: {
    filter: Filter;
    setFilter: React.Dispatch<React.SetStateAction<Filter>>;
    onlyUnfinished: boolean;
    setOnlyUnfinished: React.Dispatch<React.SetStateAction<boolean>>;
    isDistributor: boolean;
    routeAssigneeId?: string;
    routeFilter?: Partial<Filter>;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const navigate = useNavigate();
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

    const [getDistributors] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const { items: distributors } = getDistributors();
    const effectiveFilter = routeAssigneeId
        ? {
              ...filter,
              id: routeAssigneeId,
              before: filter.before ?? routeFilter?.before,
              after: filter.after ?? routeFilter?.after,
              search: filter.search || routeFilter?.search || '',
          }
        : filter;
    const selectedDistributorName = routeAssigneeId
        ? distributors.find((distributor) => distributor.id?.toString() === routeAssigneeId)?.name
        : undefined;

    const [openedKey, setOpenedKey] = useState('-1');
    const { selectedTask, setSelectedTask, setMaterials } = useContext(TasksContext);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [deletingTaskId, setDeletingTaskId] = useState<number>();
    const defaultRefetchTasks = useCallback(() => {
        resourceCache(fetchTasksAll).clear();
        resourceCache(fetchTasksYours).clear();
        resourceCache(fetchMapBranches).clear();
    }, []);
    const [activeRefetchTasks, setActiveRefetchTasks] = useState<() => void>(() => defaultRefetchTasks);
    const [missingItemsReloadKey, setMissingItemsReloadKey] = useState(0);
    const handleTasksRefetched = useCallback(() => {
        setMissingItemsReloadKey((current) => current + 1);
    }, []);
    const deleteSelectedTask = async (task: Task, refetchTasks: () => void) => {
        if (deletingTaskId !== undefined) {
            return;
        }

        setDeletingTaskId(task.id);
        try {
            await deleteTask(task);
            toast.success(<CustomToastContent content={[`${t('taskDeleted')}`]} />, toastOptionsTopRight);
            refetchTasks();
            setSelectedTask(undefined);
            setOpenedKey('-1');
        } finally {
            setDeletingTaskId(undefined);
        }
    };

    const selectTask = (task: Task) => {
        setSelectedTask(task);
        setMaterials(task?.materials);
        setOpenedKey(openedKey === task.id.toString() ? '-1' : task.id.toString());
    };

    const changeIsDoneFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked)
            setFilter(() => ({
                ...filter,
                after: dateFormat(new Date().setDate(new Date().getDate() - 7), 'yyyy-mm-dd'),
            }));
        else setFilter(() => ({ ...filter, after: '', before: '' }));
        setOnlyUnfinished(!event.target.checked);
    };

    const renderDistributorTasks = (filteredDistributors: User[]) => {
        if (filteredDistributors.length === 0) {
            return (
                <div className='h-100 d-flex align-items-center justify-content-center w-100 py-4'>
                    <span>{t('noDataText', { items: t('distributors') })}</span>
                </div>
            );
        }

        return filteredDistributors.map((distributor, index) => (
            <Button
                key={distributor.id}
                variant='light'
                type='button'
                className='my-2 d-flex align-items-center text-start w-100 border'
                onClick={() =>
                    navigate(`/tasks/distributor/${distributor.id}`, {
                        state: {
                            onlyUnfinished,
                            filter: {
                                before: filter.before,
                                after: filter.after,
                                search: filter.search,
                            },
                        },
                    })
                }
            >
                <h4 className='mb-0 px-3'>{index + 1}.</h4>
                <h4 className='mb-0 px-3'>{distributor.name}.</h4>
            </Button>
        ));
    };

    return (
        <>
            <NewRequestProvider refetchTasks={activeRefetchTasks}>
                <CompleteTaskModal
                    showModal={showCompleteModal}
                    closeModal={() => {
                        setShowCompleteModal(false);
                        activeRefetchTasks();
                    }}
                    refetchTasks={activeRefetchTasks}
                />
                <div className='w-100 d-flex position-relative flex-grow-1 pb-3 justify-content-center'>
                    <div className='w-100 d-flex flex-column  position-absolute'>
                        <div className='my-3 d-flex align-items-center justify-content-between flex-wrap gap-2'>
                            <div>
                                {routeAssigneeId && (
                                    <div className='d-flex align-items-center justify-content-between mb-3'>
                                        <Button
                                            variant='outline-secondary'
                                            type='button'
                                            onClick={() => navigate('/tasks')}
                                        >
                                            {t('previous')}
                                        </Button>
                                        {selectedDistributorName && <h5 className='mb-0 ms-3'>{selectedDistributorName}</h5>}
                                    </div>
                                )}
                                <Form.Label htmlFor='isDone' className='mx-2 mb-0'>
                                    {t('showFinishedTasks')}
                                </Form.Label>
                                <Form.Check.Input
                                    id='isDone'
                                    type='checkbox'
                                    className='my-0 mx-1'
                                    onChange={changeIsDoneFilter}
                                    checked={!onlyUnfinished}
                                />
                            </div>
                            {effectiveFilter.id && onlyUnfinished && (
                                <div className='d-flex align-items-center text-nowrap'>
                                    <Form.Label htmlFor='taskSortDirection' className='mb-0 me-2'>
                                        {t('sortDirection')}:
                                    </Form.Label>
                                    <Form.Select
                                        id='taskSortDirection'
                                        className='w-auto'
                                        value={sortDirection}
                                        onChange={(event) => setSortDirection(event.target.value as 'ASC' | 'DESC')}
                                    >
                                        <option value='ASC'>{t('ascending')}</option>
                                        <option value='DESC'>{t('descending')}</option>
                                    </Form.Select>
                                </div>
                            )}
                        </div>
                        {effectiveFilter.id ? (
                            <BranchTasksResource
                                filter={effectiveFilter}
                                onlyUnfinished={onlyUnfinished}
                                isDistributor={isDistributor}
                                sortDirection={sortDirection}
                                setActiveRefetchTasks={setActiveRefetchTasks}
                                onTasksRefetched={handleTasksRefetched}
                            >
                                {({ taskPage, refetchTasks, pagination }) => {
                                    const userTasks = taskPage.items ?? [];
                                    const tasks = userTasks.flatMap(({ notFinishedTasks, finishedTasks }) =>
                                        notFinishedTasks.concat(finishedTasks)
                                    );
                                    return (
                                        <BranchTaskList
                                            openedKey={openedKey}
                                            onlyUnfinished={onlyUnfinished}
                                            tasks={tasks}
                                            recordsCount={taskPage.pager?.totalCount ?? 0}
                                            pagination={pagination}
                                            selectTask={selectTask}
                                            selectedTaskId={selectedTask?.id || -1}
                                            deleteSelectedTask={(task) => deleteSelectedTask(task, refetchTasks)}
                                            deletingTaskId={deletingTaskId}
                                            setShowCompleteModal={setShowCompleteModal}
                                            filter={effectiveFilter}
                                            refetchTasks={refetchTasks}
                                            missingItemsReloadKey={missingItemsReloadKey}
                                            distributors={distributors}
                                            sortDirection={sortDirection}
                                        />
                                    );
                                }}
                            </BranchTasksResource>
                        ) : (
                            <DistributorsWithTasksResource
                                distributors={distributors.filter((distributor) => distributor.id !== undefined)}
                                filter={effectiveFilter}
                                onlyUnfinished={onlyUnfinished}
                            >
                                {renderDistributorTasks}
                            </DistributorsWithTasksResource>
                        )}
                    </div>
                </div>
            </NewRequestProvider>
        </>
    );
};

const BranchTaskList = ({
    openedKey,
    onlyUnfinished,
    tasks,
    recordsCount,
    pagination,
    selectTask,
    selectedTaskId,
    deleteSelectedTask,
    deletingTaskId,
    setShowCompleteModal,
    filter,
    refetchTasks,
    missingItemsReloadKey,
    distributors,
    sortDirection = 'ASC',
}: {
    openedKey: string;
    onlyUnfinished: boolean;
    tasks: Task[];
    recordsCount: number;
    pagination: ReturnType<typeof usePagination>;
    selectTask: (task: Task) => void;
    selectedTaskId: number;
    deleteSelectedTask: (task: Task) => void;
    deletingTaskId?: number;
    setShowCompleteModal: (showModal: boolean) => void;
    filter: Filter;
    refetchTasks: () => void;
    missingItemsReloadKey: number;
    distributors: User[];
    sortDirection?: 'ASC' | 'DESC';
}) => {
    const [missingItems, setMissingItems] = useState<ItemRequest[]>([]);
    const [isLoadingMissingItems, setIsLoadingMissingItems] = useState(false);
    const lastMissingItemsRequestKey = useRef<string>();

    useEffect(() => {
        let active = true;
        const requestKey = `${filter.id ?? ''}:${filter.after ?? ''}:${filter.before ?? ''}:${
            filter.search ?? ''
        }:${onlyUnfinished}:${sortDirection}:${missingItemsReloadKey}`;

        if (!onlyUnfinished || !filter.id) {
            setMissingItems([]);
            setIsLoadingMissingItems(false);
            lastMissingItemsRequestKey.current = undefined;
            return;
        }

        if (lastMissingItemsRequestKey.current === requestKey) {
            return;
        }

        lastMissingItemsRequestKey.current = requestKey;
        setIsLoadingMissingItems(true);
        setMissingItems([]);
        const assigneeId = +filter.id;

        fetchAllTasksForFilters(onlyUnfinished, filter.id, filter.before, filter.after, filter.search, undefined, sortDirection)
            .then((allTasks) => {
                const allTaskResources = allTasks.flatMap(({ neededItems }) => neededItems);

                if (allTaskResources.length === 0) {
                    return [];
                }

                return getMissingItemsForTasks(assigneeId, allTaskResources);
            })
            .then((items) => {
                if (active) {
                    setMissingItems(items ?? []);
                    setIsLoadingMissingItems(false);
                }
            })
            .catch((error) => {
                console.error('Failed to load missing items for tasks:', error);
                if (active) {
                    setIsLoadingMissingItems(false);
                    setMissingItems([]);
                    lastMissingItemsRequestKey.current = undefined;
                }
            });

        return () => {
            active = false;
        };
    }, [filter.id, filter.after, filter.before, filter.search, onlyUnfinished, missingItemsReloadKey]);

    return (
        <UserTaskList
            openedKey={openedKey}
            onlyUnfinished={onlyUnfinished}
            tasks={tasks}
            recordsCount={recordsCount}
            pagination={pagination}
            serverPagination
            selectTask={selectTask}
            selectedTaskId={selectedTaskId}
            deleteSelectedTask={deleteSelectedTask}
            deletingTaskId={deletingTaskId}
            setShowCompleteModal={setShowCompleteModal}
            missingItems={missingItems}
            isLoadingMissingItems={isLoadingMissingItems}
            filter={filter}
            refetchTasks={refetchTasks}
            distributors={distributors}
        />
    );
};

const DistributorsWithTasksResource = ({
    distributors,
    filter,
    onlyUnfinished,
    children,
}: {
    distributors: User[];
    filter: Filter;
    onlyUnfinished: boolean;
    children: (distributors: User[]) => React.ReactNode;
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [distributorIdsWithTasks, setDistributorIdsWithTasks] = useState<Set<number>>(new Set());

    useEffect(() => {
        let active = true;
        setIsLoading(true);

        fetchAssigneeIdsWithTasks(onlyUnfinished, filter.before, filter.after, filter.search)
            .then((assigneeIds) => {
                if (!active) {
                    return;
                }

                setDistributorIdsWithTasks(new Set(assigneeIds));
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [filter.after, filter.before, filter.search, onlyUnfinished]);

    const filteredDistributors = useMemo(
        () => distributors.filter((distributor) => distributor.id && distributorIdsWithTasks.has(distributor.id)),
        [distributorIdsWithTasks, distributors]
    );

    if (isLoading) {
        return (
            <div className='h-100 d-flex align-items-center justify-content-center w-100 py-4'>
                <Spinner animation='border' />
            </div>
        );
    }

    return <>{children(filteredDistributors)}</>;
};

export const RenderTaskImage = ({ branch, branchId }: { branch: Branch | BranchDetails; branchId: number }) => {
    const [getBranchImage] = useGetBranchImage(branchId);
    const image = getBranchImage();
    const [showNewRequestModal, setShowNewRequestModal] = useState(false);

    const { onBranchSelect, onClientSelect } = useContext(NewRequestContext);

    const onImageClick = () => {
        onClientSelect(branch.client);
        onBranchSelect(branch);
        setShowNewRequestModal(true);
    };

    return (
        <>
            {image ? (
                <img className='branch-img-thumb mx-2' src={image} alt='Branch' onClick={onImageClick} />
            ) : (
                <img className='branch-img-thumb mx-2' src={noImage} alt='Branch' onClick={onImageClick} />
            )}
            {showNewRequestModal && (
                <NewRequest showModal={showNewRequestModal} setShowModal={setShowNewRequestModal} />
            )}
        </>
    );
};

const TasksListEntry = ({
    task,
    isDraggable,
    selectedTaskId,
}: {
    task: Task;
    isDraggable: boolean;
    selectedTaskId: number;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });
    const branch = task.branchVO;
    const maintenanceDate = task.targetDate || branch.nextMaintenanceDate;

    const Icon = (props: { icon: IconDefinition; color?: string }) => (
        <FontAwesomeIcon
            {...props}
            className='me-2'
            style={{
                width: '20px',
                verticalAlign: 0,
            }}
            size='lg'
        />
    );
    return (
        <div className='w-100 d-flex align-items-center'>
            {isDraggable && <div className='dots' />}
            <div>
                <Icon
                    icon={
                        task.taskCategory === 'PAYMENT'
                            ? faMoneyBill
                            : task.taskCategory === 'INSTALLATION'
                            ? faCartPlus
                            : task.taskCategory === 'DEVICE_RETURN'
                            ? faUndo
                            : faTools
                    }
                />
                {task.reminders.length > 0 && <Icon icon={faBell} color='green' />}
                {task.troubleTicket && <Icon icon={faExclamationTriangle} color='red' />}
            </div>
            <ErrorBoundary fallback={<h5 className='mb-2'>Error</h5>}>
                <Suspense fallback={<Spinner animation='border' />}>
                    {task.id == selectedTaskId && <RenderTaskImage branch={branch} branchId={task.branchId} />}
                </Suspense>
            </ErrorBoundary>

            <h5 className='me-3 w-50 text-start' title={tableT('branchName')}>
                {branch.name}
            </h5>

            <div className='d-flex flex-column align-items-start w-100 mt-1'>
                <div title={tableT('companyName')}>
                    {tableT('companyName')}: {branch.client.companyName}
                </div>
                <div title={tableT('maintenanceDate')}>
                    {`${tableT('maintenanceDate')}: ${maintenanceDate ? dateFormat(maintenanceDate, date) : '-'}`}
                </div>
            </div>

            <div className='h-100 d-flex position-absolute p-1 mx-1 end-0'>
                <div>
                    <span className='text-secondary'>
                        {task.completion_time
                            ? t('ends') + '\n ' + dateFormat(task.completion_time, dateTime).split(' ')[0]
                            : t('notFinished')}
                    </span>
                    <span className='text-secondary'>
                        <br />
                        {task.completion_time && dateFormat(task.completion_time, dateTime).split(' ')[1]}
                    </span>
                </div>
            </div>
        </div>
    );
};

const UserTaskList = ({
    openedKey,
    tasks,
    recordsCount,
    pagination: externalPagination,
    serverPagination,
    filter,
    onlyUnfinished,
    selectTask,
    selectedTaskId,
    deleteSelectedTask,
    deletingTaskId,
    setShowCompleteModal,
    missingItems,
    isLoadingMissingItems = false,
    refetchTasks,
    distributors,
}: {
    openedKey: string;
    onlyUnfinished: boolean;
    tasks: Task[];
    recordsCount?: number;
    pagination?: ReturnType<typeof usePagination>;
    serverPagination?: boolean;
    selectTask: (task: Task) => void;
    selectedTaskId: number;
    deleteSelectedTask: (task: Task) => void;
    deletingTaskId?: number;
    setShowCompleteModal: (showModal: boolean) => void;
    missingItems: ItemRequest[];
    isLoadingMissingItems?: boolean;
    filter: Filter;
    refetchTasks: () => void;
    distributors: User[];
}) => {
    const localPagination = usePagination(10);
    const pagination = externalPagination ?? localPagination;
    const pager = {
        totalCount: recordsCount ?? tasks.length,
        pageStart: (pagination.currentPage - 1) * pagination.pageSize,
    };
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const items = serverPagination ? tasks : tasks.slice(pager.pageStart, pager.pageStart + pagination.pageSize);

    const [showModal, setShowModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [isDeletingSelectedTasks, setIsDeletingSelectedTasks] = useState(false);
    const [bulkAssigneeId, setBulkAssigneeId] = useState<number>();
    const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
    const transferDistributors = useMemo(
        () =>
            distributors.filter(
                (distributor) => distributor.id !== undefined && distributor.id.toString() !== filter.id
            ),
        [distributors, filter.id]
    );
    const selectedTaskIdsArray = Array.from(selectedTaskIds);

    useEffect(() => {
        setSelectedTaskIds(new Set());
    }, [filter.id, filter.after, filter.before, filter.search, onlyUnfinished]);

    useEffect(() => {
        if (!transferDistributors.some((distributor) => distributor.id === bulkAssigneeId)) {
            setBulkAssigneeId(transferDistributors[0]?.id);
        }
    }, [bulkAssigneeId, transferDistributors]);

    const toggleTaskSelection = (taskId: number) => {
        setSelectedTaskIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) {
                newSet.delete(taskId);
            } else {
                newSet.add(taskId);
            }
            return newSet;
        });
    };

    const areAllSelectedOnPage = items.length > 0 && items.every((task) => selectedTaskIds.has(task.id));

    const handleSelectAllCurrentPage = () => {
        setSelectedTaskIds((prev) => {
            const newSet = new Set(prev);
            if (areAllSelectedOnPage) {
                items.forEach((task) => newSet.delete(task.id));
            } else {
                items.forEach((task) => newSet.add(task.id));
            }
            return newSet;
        });
    };

    const handleDeleteSelected = async () => {
        if (isDeletingSelectedTasks || selectedTaskIdsArray.length === 0) {
            return;
        }

        setIsDeletingSelectedTasks(true);
        try {
            await deleteTasks(selectedTaskIdsArray);
            setSelectedTaskIds(new Set());
            refetchTasks();
            setShowModal(false);
        } finally {
            setIsDeletingSelectedTasks(false);
        }
    };

    const handleTransferSelected = async () => {
        if (!bulkAssigneeId || selectedTaskIdsArray.length === 0) {
            return;
        }

        setIsTransferring(true);
        try {
            await changeTasksAssignee(selectedTaskIdsArray, bulkAssigneeId);
            toast.success(<CustomToastContent content={[`${t('tasksTransferred')}`]} />, toastOptionsTopRight);
            setSelectedTaskIds(new Set());
            setShowTransferModal(false);
            refetchTasks();
        } finally {
            setIsTransferring(false);
        }
    };

    return (
        <Accordion activeKey={openedKey} className='tasks accordion flex-grow-1 w-100'>
            <InfoModal
                show={showModal}
                isLoading={isDeletingSelectedTasks}
                onHide={async (isDelete: boolean) => {
                    if (isDelete) {
                        await handleDeleteSelected();
                    } else {
                        setShowModal(false);
                    }
                }}
            ></InfoModal>
            <Modal show={showTransferModal} onHide={() => setShowTransferModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('transferSelectedTasks')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Label>{t('distributor')}</Form.Label>
                    <Form.Select
                        value={bulkAssigneeId ?? ''}
                        onChange={(event) => setBulkAssigneeId(Number(event.target.value))}
                    >
                        {transferDistributors.map((distributor) => (
                            <option key={distributor.id} value={distributor.id}>
                                {distributor.name}
                            </option>
                        ))}
                    </Form.Select>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowTransferModal(false)}>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant='success'
                        disabled={!bulkAssigneeId || isTransferring}
                        onClick={handleTransferSelected}
                    >
                        {isTransferring ? <Spinner animation='border' size='sm' /> : t('change')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <div className={'d-flex align-items-center'}>
                <PageSizeComponent {...{ pagination }} itemsToShow={[5, 10, 20, 50, 75, 100]} />
                <label className='w-100 fw-bold px-2'>{`${pager.pageStart + 1}-${pager.pageStart + items.length} ${t(
                    'totalCount'
                )}: ${pager.totalCount}`}</label>
            </div>
            <div className='d-flex align-items-center my-2 p-lg-3'>
                <div className='d-flex align-items-center'>
                    <Checkbox checked={areAllSelectedOnPage} onChange={handleSelectAllCurrentPage} />
                    <span className='ms-2'>{t('selectAllOnPage')}</span>
                </div>

                {selectedTaskIds.size > 0 && (
                    <div className='d-flex gap-2 ms-auto'>
                        <Button
                            variant='outline-primary'
                            disabled={transferDistributors.length === 0}
                            onClick={() => setShowTransferModal(true)}
                        >
                            {t('transferSelectedTasks')} ({selectedTaskIds.size})
                        </Button>
                        <Button variant='danger' disabled={isDeletingSelectedTasks} onClick={() => setShowModal(true)}>
                            {isDeletingSelectedTasks ? (
                                <Spinner animation='border' size='sm' />
                            ) : (
                                `${t('deleteSelected')} (${selectedTaskIds.size})`
                            )}
                        </Button>
                    </div>
                )}
            </div>
            {items.map((task, index) => (
                <Accordion.Item id={task.id.toString()} key={index} className='w-100 py-0' eventKey={`${task.id}`}>
                    <Accordion.Header className='w-100 p-1'>
                        <Checkbox
                            style={{ width: 50, height: 50 }}
                            checked={selectedTaskIds.has(task.id)}
                            onChange={() => toggleTaskSelection(task.id)}
                        />
                        <div className='d-flex align-items-center text-center w-100' onClick={() => selectTask(task)}>
                            <h5 className='mb-0 px-3'>
                                {index + pagination.pageSize * (pagination.currentPage - 1) + 1}.
                            </h5>
                            <TasksListEntry {...{ task }} selectedTaskId={selectedTaskId} isDraggable={!!filter.id} />
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className='pb-3 px-1 pt-1'>
                        <TaskDetails
                            completeSelectedTask={() => setShowCompleteModal(true)}
                            {...{
                                task,
                                deleteSelectedTask,
                            }}
                            isDeletingTask={deletingTaskId === task.id}
                            selectedTaskId={selectedTaskId}
                            refetchTasks={refetchTasks}
                            selectTask={(id) => {
                                const selectedTask = tasks.find((task) => task.id === id);
                                if (selectedTask) {
                                    selectTask(selectedTask);
                                }
                            }}
                        />
                    </Accordion.Body>
                </Accordion.Item>
            ))}
            <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
            {onlyUnfinished && (
                <MissingItemsForTasks
                    missingItems={missingItems}
                    isLoadingMissingItems={isLoadingMissingItems}
                    userId={filter.id}
                />
            )}
        </Accordion>
    );
};
